import { useEffect, useMemo, useRef } from "react";

import { useUserClassrooms } from "@features/career-details/api/use-user-classrooms";
import {
  useUpdateUserPreferences,
  useUserPreferences,
} from "@features/career-details/api/use-user-preferences";

/*
Resolves which classroom a sim pass should be generated for.

A stored preference the student is no longer enrolled in is treated as unset so
they get re-prompted instead of being stuck on a stale classroom.
*/
export const useSelectedClassroom = () => {
  const { data: classrooms = [], isPending: isLoadingClassrooms } =
    useUserClassrooms();
  const { data: preferences, isPending: isLoadingPreferences } =
    useUserPreferences();
  const { mutate: selectClassroom } = useUpdateUserPreferences();

  const selectedClassroom = useMemo(
    () =>
      classrooms.find(
        ({ classroomId }) => classroomId === preferences?.classroomId,
      ),
    [classrooms, preferences],
  );

  const isLoading = isLoadingClassrooms || isLoadingPreferences;
  const needsSelection =
    !isLoading && classrooms.length > 1 && selectedClassroom === undefined;

  const onlyClassroomId =
    classrooms.length === 1 ? classrooms[0].classroomId : undefined;

  // A student with a single classroom never sees the picker, so store it for
  // them rather than leaving the preference unset.
  //
  // The latch is what keeps this to a single request: the mutation settling
  // and its refetch landing are separate renders, so any guard based on the
  // mutation's own pending state would fire again in the gap between them.
  const attemptedClassroomId = useRef();

  useEffect(() => {
    if (isLoading || onlyClassroomId === undefined) {
      return;
    }

    if (
      preferences?.classroomId === onlyClassroomId ||
      attemptedClassroomId.current === onlyClassroomId
    ) {
      return;
    }

    attemptedClassroomId.current = onlyClassroomId;
    // Failing to persist isn't fatal — the classroom is still sent with the sim
    // pass, and the next mount retries.
    selectClassroom({ classroomId: onlyClassroomId });
  }, [isLoading, onlyClassroomId, preferences, selectClassroom]);

  return {
    classrooms,
    selectedClassroom,
    // Resolved up front so a single-classroom student goes straight to the sim
    // pass without waiting on the preference being saved.
    classroomId: selectedClassroom?.classroomId ?? onlyClassroomId,
    needsSelection,
    isLoading,
    selectClassroom,
  };
};
